import { GProductHorizontal, useBreakpointValue } from '@gazin/ecommerce-ui'
import { useCallback, useMemo } from 'react'
import { useFavorite } from 'src/contexto/hooks/useFavorite'
import { usePaymentMethod } from 'src/contexto/hooks/usePaymentMethod'
import { theme } from 'src/styles/themes/default'
import { getFavoriteStoreKey } from 'src/util/favorite'
import {
  arePixOrBilletAvailable,
  getInCashAvailablePaymentMethods,
  isCreditCardAvailable,
} from 'src/util/payment'
import {
  displayDiscountTagAndEvent,
  hasPromotionalEvent,
} from 'src/util/promotionalEvent'
import { ThumborLoader } from 'src/util/thumborLoader'
import {
  colorAndVoltageStructure,
  formatVoltage,
} from '../productVertical/utils'
import { ProductHorizontalCard } from './styles'

export type ProductCombination = {
  slug: string
  valor: string
}

export type PromoEventImage = {
  mobile?: string
  desktop?: string
}

export type ProductProps = {
  color: ProductCombination
  colorsQuantity: number
  de: number
  id: number
  imageUrl: string
  installmentMaxQuantity: number
  installmentMaxQuantityWithoutInterest: number
  installmentPriceWithInterest: number
  isAvailable: boolean
  percent: number
  por: number
  price: number
  productUrl: string
  promoEventImage?: PromoEventImage
  reviewQuantity: number
  reviewRating: number
  title: string
  trackingUrl?: string
  voltage: ProductCombination
}

export type ProductHorizontalProps = {
  product: ProductProps
  baseColor?: string
  textColor?: string
  onClick?: (product: ProductProps) => void | Promise<void>
  isBlackFriday?: boolean
  maxLinesTitle?: number
}

export const ProductHorizontal = ({
  product,
  baseColor,
  textColor,
  isBlackFriday,
  onClick,
}: ProductHorizontalProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { payment } = usePaymentMethod()
  const { favorites, favorite } = useFavorite()

  const favoriteKey = useMemo(
    () =>
      getFavoriteStoreKey(
        favorites,
        Number(product.id),
        product.color.slug,
        formatVoltage(product.voltage.slug)
      ),
    [favorites, product.color.slug, product.id, product.voltage.slug]
  )

  const handleClickFavorite = useCallback(() => {
    favorite(
      product.productUrl,
      Number(product.id),
      product.color.slug,
      formatVoltage(product.voltage.slug),
      favoriteKey
    )
  }, [
    favorite,
    favoriteKey,
    product.color.slug,
    product.id,
    product.productUrl,
    product.voltage.slug,
  ])

  const isPreviousPrice = (priceDe: number, pricePor: number) => {
    return priceDe > pricePor
  }

  return (
    <ProductHorizontalCard
      href={product.productUrl}
      onClick={() => onClick?.(product)}
      isBlackFriday={isBlackFriday}
    >
      <GProductHorizontal
        reviewQuantity={product.reviewQuantity}
        reviewRating={product.reviewRating}
        isHorizontal
        urlProduct={product.productUrl}
        isShare
        promoBadgeColor={textColor || theme.colors.white}
        de={product.de}
        por={product.por}
        price={product.price}
        installmentPriceWithInterest={product.installmentPriceWithInterest}
        installmentMaxQuantity={product.installmentMaxQuantity}
        installmentMaxQuantityWithoutInterest={
          product.installmentMaxQuantityWithoutInterest
        }
        percent={product.percent}
        title={product.title}
        negativeSign
        isFavorite
        isAvailable={product.isAvailable}
        isCreditCardPaymentAvailable={isCreditCardAvailable(payment)}
        isInCashPaymentAvailable={arePixOrBilletAvailable(payment)}
        isPreviousPrice={
          isPreviousPrice(product.de, product.por) &&
          hasPromotionalEvent(product.de, product.por)
        }
        isPromo={displayDiscountTagAndEvent({
          discountPercentage: product.percent,
          isAvailable: product.isAvailable,
        })}
        onClick={() => ({})}
        onClickFavorite={handleClickFavorite}
        variant={favoriteKey >= 0 ? 'filled' : 'outline'}
        variantPromo={baseColor || theme.success.main}
        voltageColorVariation={colorAndVoltageStructure(product)}
        colorsQuantity={product.colorsQuantity}
        productUnavailableDescription="Produto Indisponível"
        cashPaymentDescription={`No ${getInCashAvailablePaymentMethods(
          payment
        )}`}
        desktopImage={product.promoEventImage?.desktop}
        mobileImage={product.promoEventImage?.mobile}
        urlImage={ThumborLoader({
          src: product.imageUrl,
          width: isMobile ? 200 : 300,
          quality: 75,
        })}
      />
    </ProductHorizontalCard>
  )
}
